import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

/**
 * Replaces the app's Alert.alert. `toast()` shows a transient message;
 * `confirm()` returns a promise resolving true/false and is rendered by
 * ConfirmDialog in App.js.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const resolver = useRef(null);
  const seq = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, opts = {}) => {
    const id = ++seq.current;
    const item = {
      id,
      message,
      title: opts.title,
      tone: opts.tone || 'default', // 'default' | 'success' | 'error'
    };
    setToasts((list) => [...list, item]);
    setTimeout(() => dismiss(id), opts.duration || 3800);
    return id;
  }, [dismiss]);

  const confirm = useCallback((options) => {
    // Accepts a string or { title, message, confirmLabel, cancelLabel, danger }.
    const cfg = typeof options === 'string' ? { title: options } : options || {};
    setDialog({
      title: cfg.title || 'Are you sure?',
      message: cfg.message || '',
      confirmLabel: cfg.confirmLabel || 'Confirm',
      cancelLabel: cfg.cancelLabel || 'Cancel',
      danger: !!cfg.danger,
    });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const closeDialog = useCallback((result) => {
    setDialog(null);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  }, []);

  return (
    <ToastContext.Provider value={{ toast, confirm, toasts, dismiss, dialog, closeDialog }}>
      {children}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
